import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CalendarDays, Upload, Loader2, Trash2, Eye, EyeOff,
  Image, Video, Save, Clock, Mic2, FileText, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TodaysRoomAdmin = ({ token }) => {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const fileRef = useRef(null);
  const posterRef = useRef(null);

  const fetchPosts = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/admin/todays-room`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setPosts(d); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const createNew = async () => {
    const res = await fetch(`${API}/api/admin/todays-room`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const post = await res.json();
      setEditing(post);
      fetchPosts();
      toast.success('New room post created');
    }
  };

  const saveFields = async () => {
    if (!editing) return;
    setSaving(true);
    const params = new URLSearchParams();
    ['title', 'description', 'date', 'time', 'host', 'additional_details', 'media_type'].forEach(f => {
      if (editing[f] !== undefined && editing[f] !== null) params.set(f, editing[f]);
    });
    const res = await fetch(`${API}/api/admin/todays-room/${editing.id}?${params}`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const updated = await res.json();
      setEditing(updated);
      fetchPosts();
      toast.success('Saved');
    } else { toast.error('Save failed'); }
    setSaving(false);
  };

  const uploadMedia = async (file) => {
    if (!editing) return;
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API}/api/admin/todays-room/${editing.id}/upload-media`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
    });
    if (res.ok) {
      const updated = await res.json();
      setEditing(updated);
      fetchPosts();
      toast.success('Media uploaded');
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const uploadPoster = async (file) => {
    if (!editing) return;
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API}/api/admin/todays-room/${editing.id}/upload-poster`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
    });
    if (res.ok) {
      const updated = await res.json();
      setEditing(updated);
      toast.success('Poster uploaded');
    } else { toast.error('Poster upload failed'); }
  };

  const publish = async (id) => {
    const res = await fetch(`${API}/api/admin/todays-room/${id}/publish`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const updated = await res.json();
      if (editing?.id === id) setEditing(updated);
      fetchPosts();
      toast.success('Published — now live on homepage');
    } else { toast.error('Publish failed'); }
  };

  const unpublish = async (id) => {
    const res = await fetch(`${API}/api/admin/todays-room/${id}/unpublish`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      if (editing?.id === id) setEditing(prev => ({ ...prev, status: 'draft', is_current: false }));
      fetchPosts();
      toast.success('Unpublished');
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this room post permanently?')) return;
    await fetch(`${API}/api/admin/todays-room/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    if (editing?.id === id) setEditing(null);
    fetchPosts();
    toast.success('Deleted');
  };

  const update = (field, val) => setEditing(prev => ({ ...prev, [field]: val }));

  const currentPost = posts.find(p => p.is_current && p.status === 'published');
  const archivePosts = posts.filter(p => !p.is_current || p.status !== 'published');

  return (
    <div data-testid="todays-room-admin">
      <h2 className="text-white font-bold text-lg mb-1 flex items-center gap-2">
        <CalendarDays size={18} className="text-[#F97316]" /> Today's Room
      </h2>
      <p className="text-white/30 text-xs mb-5">Manage the featured room post on the homepage.</p>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="text-[#F97316] animate-spin" /></div>
      ) : (
        <>
          {/* Current post quick view */}
          {currentPost && !editing && (
            <div className="glass-card p-4 mb-5 border-[#F97316]/20" data-testid="current-room-card">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-green-400/10 text-green-400">LIVE</span>
                <span className="text-white font-semibold text-sm">{currentPost.title || 'Untitled'}</span>
              </div>
              <div className="flex items-center gap-2 text-white/30 text-[10px] mb-3">
                {currentPost.date && <span>{currentPost.date}</span>}
                {currentPost.media_type && <span className="uppercase">{currentPost.media_type}</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(currentPost)} className="px-3 py-1.5 text-xs font-semibold text-white/60 bg-white/5 hover:bg-white/10 rounded-lg transition-colors" data-testid="edit-current-room">Edit</button>
                <button onClick={() => unpublish(currentPost.id)} className="px-3 py-1.5 text-xs font-semibold text-amber-400 bg-amber-400/10 hover:bg-amber-400/20 rounded-lg transition-colors" data-testid="unpublish-room">Unpublish</button>
              </div>
            </div>
          )}

          {/* Create new */}
          {!editing && (
            <button onClick={createNew} className="btn-pink !text-xs mb-5" data-testid="create-room-post">
              <CalendarDays size={14} /> Create New Room Post
            </button>
          )}

          {/* Editor */}
          {editing && (
            <div className="glass-card p-5 mb-5 space-y-4" data-testid="room-editor">
              <div className="flex items-center justify-between">
                <h3 className="text-white text-sm font-bold">
                  {editing.status === 'published' ? 'Edit Published Post' : 'Edit Draft'}
                </h3>
                <button onClick={() => setEditing(null)} className="text-white/30 hover:text-white text-xs">Close</button>
              </div>

              {/* Media type toggle */}
              <div>
                <label className="text-white/40 text-xs block mb-2">Media Type</label>
                <div className="flex gap-2">
                  <button onClick={() => update('media_type', 'image')}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                      editing.media_type === 'image' ? 'bg-[#F97316]/10 text-[#F97316] border-[#F97316]/30' : 'bg-white/5 text-white/40 border-white/10'
                    }`} data-testid="media-type-image">
                    <Image size={14} /> Image / Flyer
                  </button>
                  <button onClick={() => update('media_type', 'video')}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                      editing.media_type === 'video' ? 'bg-[#F97316]/10 text-[#F97316] border-[#F97316]/30' : 'bg-white/5 text-white/40 border-white/10'
                    }`} data-testid="media-type-video">
                    <Video size={14} /> Video
                  </button>
                </div>
              </div>

              {/* Upload */}
              <div>
                <label className="text-white/40 text-xs block mb-2">
                  {editing.media_type === 'video' ? 'Upload Video' : 'Upload Flyer'}
                </label>
                <input ref={fileRef} type="file"
                  accept={editing.media_type === 'video' ? 'video/mp4,video/quicktime,video/webm' : 'image/jpeg,image/png,image/webp'}
                  className="hidden" onChange={e => e.target.files[0] && uploadMedia(e.target.files[0])} />
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed border-white/10 hover:border-[#F97316]/30 text-white/40 hover:text-[#F97316] text-xs transition-all"
                  data-testid="upload-media-btn">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {uploading ? 'Uploading...' : `Choose ${editing.media_type === 'video' ? 'Video' : 'Image'} File`}
                </button>
              </div>

              {/* Preview */}
              {editing.media_url && (
                <div data-testid="media-preview">
                  <label className="text-white/40 text-xs block mb-2">Preview</label>
                  {editing.media_type === 'video' ? (
                    <video src={editing.media_url} poster={editing.poster_url || undefined} controls playsInline
                      className="w-full rounded-lg" style={{ maxHeight: 300, objectFit: 'contain' }} data-testid="preview-video" />
                  ) : (
                    <img src={editing.media_url} alt="Preview" className="w-full rounded-lg" style={{ objectFit: 'contain' }} data-testid="preview-image" />
                  )}
                </div>
              )}

              {/* Video poster upload */}
              {editing.media_type === 'video' && (
                <div>
                  <label className="text-white/40 text-xs block mb-2">Poster / Thumbnail (optional)</label>
                  <input ref={posterRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                    onChange={e => e.target.files[0] && uploadPoster(e.target.files[0])} />
                  <button onClick={() => posterRef.current?.click()}
                    className="px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20 text-white/40 text-xs transition-colors"
                    data-testid="upload-poster-btn">
                    <Upload size={12} className="inline mr-1" /> Upload Poster
                  </button>
                  {editing.poster_url && <img src={editing.poster_url} alt="Poster" className="mt-2 w-24 rounded" />}
                </div>
              )}

              {/* Text fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-white/40 text-xs block mb-1">Room Title</label>
                  <input type="text" value={editing.title || ''} onChange={e => update('title', e.target.value)}
                    placeholder="e.g. Friday Night Vibes" className="input-med w-full !text-sm" data-testid="room-title-input" />
                </div>
                <div>
                  <label className="text-white/40 text-xs block mb-1">Date</label>
                  <input type="text" value={editing.date || ''} onChange={e => update('date', e.target.value)}
                    placeholder="e.g. July 20, 2026" className="input-med w-full !text-sm" data-testid="room-date-input" />
                </div>
                <div>
                  <label className="text-white/40 text-xs block mb-1">Time</label>
                  <input type="text" value={editing.time || ''} onChange={e => update('time', e.target.value)}
                    placeholder="e.g. 9:00 PM EST" className="input-med w-full !text-sm" data-testid="room-time-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-white/40 text-xs block mb-1">Host</label>
                  <input type="text" value={editing.host || ''} onChange={e => update('host', e.target.value)}
                    placeholder="e.g. DJ ChopSuey" className="input-med w-full !text-sm" data-testid="room-host-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-white/40 text-xs block mb-1">Description</label>
                  <textarea value={editing.description || ''} onChange={e => update('description', e.target.value)}
                    placeholder="What's happening in tonight's room?" rows={3}
                    className="input-med w-full !text-sm resize-none" data-testid="room-description-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-white/40 text-xs block mb-1">Additional Details (optional)</label>
                  <textarea value={editing.additional_details || ''} onChange={e => update('additional_details', e.target.value)}
                    placeholder="Special guests, rules, links..." rows={2}
                    className="input-med w-full !text-sm resize-none" data-testid="room-details-input" />
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                <button onClick={saveFields} disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-semibold transition-colors"
                  data-testid="save-room-btn">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                </button>
                {editing.status !== 'published' ? (
                  <button onClick={() => publish(editing.id)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#F97316]/20 hover:bg-[#F97316]/30 text-[#F97316] text-xs font-semibold transition-colors"
                    data-testid="publish-room-btn">
                    <Eye size={12} /> Publish
                  </button>
                ) : (
                  <button onClick={() => unpublish(editing.id)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-400/10 hover:bg-amber-400/20 text-amber-400 text-xs font-semibold transition-colors"
                    data-testid="unpublish-room-btn">
                    <EyeOff size={12} /> Unpublish
                  </button>
                )}
                <button onClick={() => deletePost(editing.id)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-semibold transition-colors ml-auto"
                  data-testid="delete-room-btn">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          )}

          {/* Archive */}
          {archivePosts.length > 0 && (
            <div>
              <button onClick={() => setShowArchive(!showArchive)}
                className="flex items-center gap-2 text-white/30 hover:text-white/50 text-xs font-semibold mb-3 transition-colors"
                data-testid="toggle-archive">
                {showArchive ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                Archive ({archivePosts.length})
              </button>
              {showArchive && (
                <div className="space-y-2">
                  {archivePosts.map(p => (
                    <div key={p.id} className="glass-card px-4 py-3 flex items-center gap-3" data-testid={`archive-${p.id}`}>
                      {p.media_url && p.media_type === 'image' && (
                        <img src={p.media_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                      )}
                      {p.media_url && p.media_type === 'video' && (
                        <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                          <Video size={14} className="text-white/30" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs font-medium truncate">{p.title || 'Untitled'}</p>
                        <p className="text-white/20 text-[10px]">
                          {p.date || 'No date'} &middot; {p.media_type} &middot;{' '}
                          <span className={p.status === 'published' ? 'text-green-400' : 'text-white/30'}>{p.status}</span>
                        </p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => setEditing(p)} className="px-2 py-1 text-[10px] text-white/40 hover:text-white bg-white/5 hover:bg-white/10 rounded transition-colors">Edit</button>
                        <button onClick={() => publish(p.id)} className="px-2 py-1 text-[10px] text-[#F97316] hover:bg-[#F97316]/10 rounded transition-colors">Publish</button>
                        <button onClick={() => deletePost(p.id)} className="px-2 py-1 text-[10px] text-red-400 hover:bg-red-400/10 rounded transition-colors"><Trash2 size={10} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TodaysRoomAdmin;
